/**
 * Shared integration configuration.
 *
 * Extracted from IntegrationsSettings.tsx so both the Settings page and the
 * Chat ToolPicker can reference the same service definitions and types.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ServiceConfig {
  id: string;
  name: string;
  description: string;
  icon: string;
  supportsUserSearch: boolean;
  endpoints: {
    status: string;
    authorize: string;
    revoke: string;
    settings?: string;
  };
}

export interface IntegrationStatus {
  connected: boolean;
  integration_type: string;
  email?: string;
  connected_at?: string;
  settings?: {
    supports_all_drives?: boolean;
    cloud_id?: string;
    organization?: string;
  };
  token_status?: "active" | "expired" | "refresh_failed" | "no_token";
  token_expiry?: string;
}

// ---------------------------------------------------------------------------
// Service definitions (OAuth-capable integrations)
// ---------------------------------------------------------------------------

export const SERVICES: ServiceConfig[] = [
  {
    id: "google_drive",
    name: "Google Drive",
    description: "Access and sync files from Google Drive",
    icon: "logos:google-drive",
    supportsUserSearch: false,
    endpoints: {
      status: "/admin/v1/integrations/google-drive/status",
      authorize: "/admin/v1/integrations/google-drive/authorize",
      revoke: "/admin/v1/integrations/google-drive/revoke",
      settings: "/admin/v1/integrations/google-drive/settings",
    },
  },
  {
    id: "jira",
    name: "Atlassian",
    description: "Access Jira issues, Confluence pages, and search users",
    icon: "logos:atlassian",
    supportsUserSearch: true,
    endpoints: {
      status: "/admin/v1/integrations/jira/status",
      authorize: "/admin/v1/integrations/jira/authorize",
      revoke: "/admin/v1/integrations/jira/revoke",
    },
  },
  {
    id: "github",
    name: "GitHub",
    description: "Access repositories and search users",
    icon: "logos:github-icon",
    supportsUserSearch: true,
    endpoints: {
      status: "/admin/v1/integrations/github/status",
      authorize: "/admin/v1/integrations/github/authorize",
      revoke: "/admin/v1/integrations/github/revoke",
    },
  },
  {
    id: "google_calendar",
    name: "Google Calendar",
    description: "Access and manage calendar events and scheduling",
    icon: "logos:google-calendar",
    supportsUserSearch: false,
    endpoints: {
      status: "/admin/v1/integrations/google-calendar/status",
      authorize: "/admin/v1/integrations/google-calendar/authorize",
      revoke: "/admin/v1/integrations/google-calendar/revoke",
    },
  },
  {
    id: "ms_calendar",
    name: "Microsoft Calendar",
    description: "Access and manage Microsoft 365 calendar events",
    icon: "logos:microsoft-icon",
    supportsUserSearch: false,
    endpoints: {
      status: "/admin/v1/integrations/ms-calendar/status",
      authorize: "/admin/v1/integrations/ms-calendar/authorize",
      revoke: "/admin/v1/integrations/ms-calendar/revoke",
    },
  },
];

// ---------------------------------------------------------------------------
// Tool ID → Integration service ID mapping
// ---------------------------------------------------------------------------

/**
 * Maps platform tool IDs to the integration service ID that provides OAuth.
 * Only tools requiring OAuth are listed — tools not in this map (web_search,
 * knowledge_base, etc.) can be toggled without any OAuth check.
 *
 * Keys must be normalised (lowercase, underscores, no trailing `_tool`).
 * Includes both direct IDs and MCP-prefixed variants (e.g. "mcp_gdrive").
 */
export const TOOL_TO_INTEGRATION_MAP: Record<string, string> = {
  // Direct tool IDs
  google_drive: "google_drive",
  gdrive: "google_drive",
  github: "github",
  jira: "jira",
  confluence: "jira", // Confluence uses the Atlassian (Jira) OAuth
  google_calendar: "google_calendar",
  gcalendar: "google_calendar",
  ms_calendar: "ms_calendar",
  mscalendar: "ms_calendar",
  // MCP-prefixed variants
  mcp_gdrive: "google_drive",
  mcp_google_drive: "google_drive",
  mcp_github: "github",
  mcp_jira: "jira",
  mcp_confluence: "jira",
  mcp_google_calendar: "google_calendar",
  mcp_gcalendar: "google_calendar",
  mcp_ms_calendar: "ms_calendar",
  mcp_mscalendar: "ms_calendar",

  // ===== Google Drive MCP Tools =====
  search_google_drive: "google_drive",
  read_google_drive_file: "google_drive",
  create_google_drive_file: "google_drive",
  edit_google_drive_file: "google_drive",
  get_google_drive_file_metadata: "google_drive",
  update_google_drive_file_metadata: "google_drive",
  create_google_drive_folder: "google_drive",
  list_google_drive_folder: "google_drive",
  get_google_drive_folder_tree: "google_drive",
  create_google_drive_permission: "google_drive",

  // ===== Google Calendar MCP Tools =====
  gcal_list_calendars: "google_calendar",
  gcal_get_calendar: "google_calendar",
  gcal_list_events: "google_calendar",
  gcal_search_events: "google_calendar",
  gcal_get_event: "google_calendar",
  gcal_list_event_instances: "google_calendar",
  gcal_create_event: "google_calendar",
  gcal_quick_add_event: "google_calendar",
  gcal_create_event_with_meet: "google_calendar",
  gcal_update_event: "google_calendar",
  gcal_delete_event: "google_calendar",
  gcal_rsvp_event: "google_calendar",
  gcal_get_free_busy: "google_calendar",
  gcal_get_settings: "google_calendar",

  // ===== GitHub MCP Tools =====
  github_list_repos: "github",
  github_get_repo: "github",
  github_search_repos: "github",
  github_create_repository: "github",
  github_list_issues: "github",
  github_get_issue: "github",
  github_create_issue: "github",
  github_update_issue: "github",
  github_search_issues: "github",
  github_add_issue_comment: "github",
  github_list_issue_comments: "github",
  github_list_prs: "github",
  github_get_pr: "github",
  github_create_pr: "github",
  github_merge_pr: "github",
  github_list_pr_commits: "github",
  github_list_pr_files: "github",
  github_get_file: "github",
  github_search_code: "github",
  github_create_file: "github",
  github_get_tree: "github",
  github_list_commits: "github",
  github_get_commit: "github",
  github_search_users: "github",
  github_list_branches: "github",

  // ===== Jira MCP Tools =====
  jira_list_projects: "jira",
  jira_get_project: "jira",
  jira_create_project: "jira",
  jira_search_issues: "jira",
  jira_get_issue: "jira",
  jira_create_issue: "jira",
  jira_update_issue: "jira",
  jira_get_comments: "jira",
  jira_add_comment: "jira",
  jira_get_transitions: "jira",
  jira_transition_issue: "jira",

  // ===== Confluence MCP Tools =====
  confluence_list_spaces: "jira", // Confluence uses Jira OAuth
  confluence_search_pages: "jira",
  confluence_get_page: "jira",
  confluence_read_page_content: "jira",
  confluence_get_pages_in_space: "jira",
  confluence_create_page: "jira",
  confluence_update_page: "jira",
  confluence_get_page_comments: "jira",
  confluence_add_comment: "jira",
  confluence_get_page_restrictions: "jira",
  confluence_add_page_restriction: "jira",
  confluence_update_page_restrictions: "jira",
  confluence_delete_page_restrictions: "jira",

  // ===== Microsoft Calendar MCP Tools =====
  mscal_get_current_user: "ms_calendar",
  mscal_list_calendars: "ms_calendar",
  mscal_get_calendar: "ms_calendar",
  mscal_list_events: "ms_calendar",
  mscal_get_calendar_view: "ms_calendar",
  mscal_get_event: "ms_calendar",
  mscal_search_events: "ms_calendar",
  mscal_create_event: "ms_calendar",
  mscal_create_recurring_event: "ms_calendar",
  mscal_update_event: "ms_calendar",
  mscal_delete_event: "ms_calendar",
  mscal_accept_event: "ms_calendar",
  mscal_decline_event: "ms_calendar",
  mscal_tentatively_accept: "ms_calendar",
  mscal_get_schedule: "ms_calendar",
  mscal_find_meeting_times: "ms_calendar",

  // ===== Snowflake MCP Tools =====
  // Snowflake uses service credentials (like Slack), NOT OAuth.
  // Intentionally NOT mapped here — presence in this map triggers
  // the "Not connected" chip which requires OAuth status checks.
};

/**
 * Normalise a tool ID so it matches TOOL_TO_INTEGRATION_MAP keys regardless
 * of the format returned by the platform API (e.g. "google_drive_tool",
 * "Google-Drive", "google_drive").
 */
export function normaliseToolId(toolId: string): string {
  return toolId
    .toLowerCase()
    .replace(/-/g, "_") // kebab-case → snake_case
    .replace(/_tool$/, ""); // strip trailing _tool suffix
}

/**
 * Look up the ServiceConfig for a given tool ID.
 * Returns `undefined` if the tool does not require OAuth.
 */
export function getServiceForTool(toolId: string): ServiceConfig | undefined {
  const normalised = normaliseToolId(toolId);
  const integrationId = TOOL_TO_INTEGRATION_MAP[normalised];

  if (!integrationId) return undefined;

  return SERVICES.find((s) => s.id === integrationId);
}

/**
 * Check whether a tool ID requires OAuth.
 */
export function toolRequiresOAuth(toolId: string): boolean {
  return normaliseToolId(toolId) in TOOL_TO_INTEGRATION_MAP;
}
